import React from "react";
import { Platform, View } from "react-native";
import dayjs from "dayjs";
import {
  Link,
  Panel,
  Text,
  theme,
  Pill,
  Heading,
} from "@clearabee/ui-library-native";
import { Table } from "@clearabee/ui-library/src/Composite/Table/Table";
import { Anchor } from "components/Core/Anchor/Anchor";
import { SkeletonLoader } from "components/SkeletonLoader/SkeletonLoader";
import { Rect } from "react-native-svg";
import { useTranslation } from "react-i18next";
import { styles } from "./JobsView.styles";
import { useRouter } from "lib/hooks/useRouter";
import {
  convertApiStatus,
  getTopCategoryFromItems,
  formatPostcode,
} from "lib/utils";
import { JobsViewProps } from "./JobsView.types";
import { useCatalogue } from "@clearabee/ui-sdk";
import { STATUS_COLORS } from "lib/constants/statusColors";

export const LargeScreenView = ({
  skeletonSize,
  jobs,
  viewAllLink = true,
}: JobsViewProps): React.ReactElement => {
  const [translate] = useTranslation();
  const { navigate } = useRouter("JobHistory");
  const catalogue = useCatalogue();

  return (
    <Panel>
      <View style={styles.headingWrapper}>
        {viewAllLink && (
          <View>
            <Heading fontSize="xsmall2" styles={styles.subheading}>
              {translate("hwa.screens.jobHistory.title")}
            </Heading>
            <Heading
              color="brand"
              fontSize="base"
              level={5}
              styles={{ fontFamily: "ProximaSemiBold" }}
            >
              {translate("hwa.components.viewJobs.yourPreviousJobs")}
            </Heading>
          </View>
        )}
        {jobs?.length !== 0 && viewAllLink && (
          <Link underline>
            <Anchor routeName="JobHistory" params={{ status: "Completed" }}>
              <Text color="brand" fontSize="small">
                {translate("common.viewAll")}
              </Text>
            </Anchor>
          </Link>
        )}
      </View>
      <Table>
        <colgroup>
          <col style={{ width: "15%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "auto" }} />
          <col style={{ width: "15%" }} />
          <col style={{ width: "15%" }} />
        </colgroup>
        {!viewAllLink && (
          <Table.Header
            fontSize="xsmall"
            headings={[
              translate("common.jobReference"),
              translate("common.serviceType"),
              translate("common.postcode"),
              translate("common.date"),
              translate("common.time"),
              translate("common.status"),
            ]}
          />
        )}
        <Table.Body>
          {jobs?.map((job, index) => (
            <Table.Row
              key={`${job.ref}-${index}`}
              styles={[
                styles.tableRow,
                index + 1 === jobs.length && {
                  border: "none",
                },
                Platform.OS === "web" && { cursor: "pointer" },
              ]}
              onClick={() => {
                if (job.id) {
                  navigate({
                    routeName: "Job",
                    params: { ref: job.ref },
                  });
                }
              }}
            >
              <Table.Cell.Text>{job.ref}</Table.Cell.Text>
              <Table.Cell.Text>
                {getTopCategoryFromItems(
                  catalogue.defaultCatalogue,
                  job?.items ?? [],
                )?.title ?? ""}
              </Table.Cell.Text>
              <Table.Cell.Text>
                {formatPostcode(job.addressPostcode)}
              </Table.Cell.Text>
              <Table.Cell.Text>
                {dayjs(job.date).format("ddd, Do MMMM YYYY")}
              </Table.Cell.Text>
              <Table.Cell.Text>
                {job.timePreference === "Any" &&
                  translate(
                    "hwa.screens.dashboard.userDashboard.timeslotPlaceholder",
                  )}
                {job.timePreference !== "Any" &&
                  job.timePreference !== "" &&
                  job.timePreference}
                {job.timeslotJson &&
                  `${dayjs(`2020-01-01T${job.timeslotJson.from}`).format(
                    "ha",
                  )} - ${dayjs(`2020-01-01T${job.timeslotJson.to}`).format(
                    "ha",
                  )}`}
              </Table.Cell.Text>
              <Table.Cell styles={{ display: "flex", alignItems: "center" }}>
                <Pill
                  styles={{ flex: 1, alignItems: "center" }}
                  color={
                    STATUS_COLORS[job.status as keyof typeof STATUS_COLORS]
                  }
                >
                  {convertApiStatus(job.status) ?? job.status}
                </Pill>
              </Table.Cell>
            </Table.Row>
          ))}
          {jobs?.length === 0 && (
            <Table.Row styles={[styles.tableRow, { border: "none" }]}>
              <Table.Cell
                colSpan={6}
                styles={{
                  textAlign: "center",
                  paddingTop: theme.spacing.large,
                }}
              >
                {translate("hwa.components.jobsView.noJobsFound")}
              </Table.Cell>
            </Table.Row>
          )}
          {!jobs &&
            Array.from(Array(skeletonSize).keys()).map((row, index) => (
              <Table.Row
                key={row}
                styles={[
                  styles.tableRow,
                  skeletonSize === index + 1 && { border: "none" },
                ]}
              >
                <Table.Cell colSpan={6}>
                  <SkeletonLoader
                    speed={1}
                    backgroundColor={theme.colors.light.base}
                    foregroundColor={`${theme.colors.greyscale.lighter}00`}
                    height={29.5}
                    width="100%"
                  >
                    <Rect
                      x="0"
                      y="0"
                      rx="8"
                      ry="8"
                      width="100%"
                      height="100%"
                    />
                  </SkeletonLoader>
                </Table.Cell>
              </Table.Row>
            ))}
        </Table.Body>
      </Table>
    </Panel>
  );
};
